import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import auth from "services/auth";
import ls from "services/ls";
import AppContext from "contexts/AppContext";
import api from "services/api";

import { Button } from "react-bootstrap";
import { __ } from "services/Translator";

import FacebookLogin from "react-facebook-login";
import config from "configs/config.js";
import jwt_decode from "jwt-decode";
import swal from "sweetalert2";
import { sanitizeTextInput } from "utils/profileValidation";
import { getEnabledSocialProviders, getConfiguredSocialProviders } from "utils/signupFlow";
import { getProviderMeta } from "configs/socialProviders";
import { startCodeFlow } from "utils/socialOauth";
import { getInitialSettings } from "utils/initialSettings";

// Betmode (cashier-style) social icon rail. The tiles are icon-only, so the
// brand mark IS the button; each carries its own accessible name. Order and
// colours are fixed to match the confirmed cashier redesign mockup.
const BETMODE_BRAND = {
  google: "#4285F4",
  facebook: "#1877F2",
  discord: "#5865F2",
  twitch: "#9146FF",
  kick: "#53FC18",
  x: "#ffffff",
};
const BETMODE_LABEL = {
  google: "Google",
  facebook: "Facebook",
  discord: "Discord",
  twitch: "Twitch",
  kick: "Kick",
  x: "X",
};
// Canonical position of the authorization-code providers inside the rail.
const BETMODE_CODE_ORDER = ["discord", "twitch", "kick", "x"];
// Self-coloured brand marks (fills baked in, exactly like the mockup); the
// per-tile --sl-brand / --b drives the hover tint, not the glyph colour.
const BETMODE_ICON_SPEC = {
  google: {
    vb: "0 0 18 18",
    paths: [
      { fill: "#4285F4", d: "M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" },
      { fill: "#34A853", d: "M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" },
      { fill: "#FBBC05", d: "M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" },
      { fill: "#EA4335", d: "M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" },
    ],
  },
  facebook: {
    vb: "0 0 24 24",
    paths: [{ fill: "#1877F2", d: "M24 12a12 12 0 1 0-13.875 11.854v-8.385H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.313 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385A12.002 12.002 0 0 0 24 12z" }],
  },
  discord: {
    vb: "0 0 24 24",
    paths: [{ fill: "#5865F2", d: "M20.317 4.369A19.79 19.79 0 0 0 15.885 3a13.9 13.9 0 0 0-.617 1.269 18.27 18.27 0 0 0-5.532 0A13.9 13.9 0 0 0 9.11 3a19.74 19.74 0 0 0-4.432 1.369C1.869 8.62 1.104 12.766 1.486 16.853a19.9 19.9 0 0 0 6.073 3.058c.492-.669.93-1.38 1.307-2.127a12.9 12.9 0 0 1-2.06-.99c.173-.126.342-.257.505-.39a14.2 14.2 0 0 0 12.087 0c.165.135.334.266.505.39-.657.388-1.35.72-2.062.99.377.747.816 1.457 1.307 2.127a19.85 19.85 0 0 0 6.075-3.058c.448-4.757-.766-8.866-3.208-12.484zM8.02 14.34c-1.183 0-2.157-1.085-2.157-2.42 0-1.334.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.335-.955 2.42-2.157 2.42zm7.96 0c-1.183 0-2.157-1.085-2.157-2.42 0-1.334.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.335-.946 2.42-2.157 2.42z" }],
  },
  twitch: {
    vb: "0 0 24 24",
    paths: [{ fill: "#9146FF", d: "M4.265 0 1.5 3.69v14.4h4.8V22.5h2.7l3.585-4.41h4.05L23.25 12V0zm16.8 11.1-3.6 4.5h-4.05l-3.6 4.5v-4.5H5.7V1.8h15.365z" }],
  },
  kick: {
    vb: "0 0 24 24",
    paths: [{ fill: "#53FC18", d: "M3 3h6v6h3V6h3V3h6v9h-3v3h3v9h-6v-3h-3v-3H9v6H3z" }],
  },
  x: {
    vb: "0 0 24 24",
    paths: [{ fill: "#ffffff", d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" }],
  },
};

// A brand mark for the rail. `labelled` makes the SVG itself the button's
// accessible name (used for the Facebook tile, whose button is owned by the
// react-facebook-login component and cannot take an aria-label directly).
const renderBetmodeIcon = (providerKey, labelled) => {
  const spec = BETMODE_ICON_SPEC[providerKey];
  if (!spec) return null;
  const a11y = labelled
    ? { role: "img", "aria-label": __(BETMODE_LABEL[providerKey]) }
    : { "aria-hidden": true };
  return (
    <svg className="betmode-auth__social-icon" width="21" height="21" viewBox={spec.vb} {...a11y}>
      {spec.paths.map((p, i) => (
        <path key={i} fill={p.fill} d={p.d} />
      ))}
    </svg>
  );
};

const SocialLogin = (props) => {
  const {
    bonusOffer,
    referralCode,
    variant = "classic",
    googleButtonId = "googleLoginModal",
    showOrDivider = true,
  } = props;

  const history = useHistory();
  const { updateUserAuthStatus } = React.useContext(AppContext);
  const [loginData, setLoginData] = useState({});

  const buildSocialSignupPayload = (data) => {
    const payload = {
      ...data,
      agreeToTerms: data.agreeToTerms ? 1 : 0,
      acceptCountryLimitation: data.acceptCountryLimitation ? 1 : 0,
    };

    if (bonusOffer) {
      payload.bonusOffer = bonusOffer;
    }

    const sanitizedReferral = sanitizeTextInput(referralCode, 80);
    if (sanitizedReferral) {
      payload.referralCode = sanitizedReferral;
    }

    const affToken = ls.get("afftoken");
    if (affToken) {
      payload.affiliateToken = affToken;
    }

    return payload;
  };

  const socialLoginAction = async (loginData) => {
    var response = await api.post("/social-login", loginData);
    if (response.data.status == "success") {
      await auth.login(response.data.user);
      updateUserAuthStatus(response.data.user);
      // Land on the same destination as email/password + email-OTP auth.
      history.push("/games/all");
    } else {
      return new swal({
        className: "custom_swal",
        title: __("Failed!"),
        text: response.data.message,
      });
    }
  };

  // Prefer settings supplied by the parent (avoids a duplicate fetch and the
  // brief flash where providers render before the brand config arrives). Fall
  // back to a self-fetch for callers that do not pass them (e.g. banner login).
  const [fetchedSettings, setFetchedSettings] = useState(null);
  const initialSettings = props.initialSettings || fetchedSettings || {};

  useEffect(() => {
    if (!props.initialSettings) {
      getInitialSettings().then(setFetchedSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialSettings]);

  const [spinner, setSpinner] = useState(false);
  const [showSocialRegTerms, setShowSocialRegTerms] = useState(false);
  const [socialRegisterData, setSocialRegisterData] = useState({
    name: "",
    email: "",
    socialId: "",
    provider: "",
    agreeToTerms: 0,
    acceptCountryLimitation: 0,
  });

  const responseFacebook = (response) => {
    // Forward the RAW Facebook access token. The backend verifies it server-side
    // via the Graph API and derives the identity from the verified response; the
    // client-supplied email/socialId are no longer trusted.
    const data = {
      name: response.name,
      email: response.email,
      socialId: response.userID,
      token: response.accessToken,
      provider: "Facebook",
      agreeToTerms: 0,
      acceptCountryLimitation: 0,
    };
    setSocialRegisterData(data);
    setRegisterData(data);
  };

  const componentClicked = () => {
    setSpinner(true);
  };

  const responseGoogle = (response) => {
    // response.credential is the raw Google id_token (JWT). We still decode it
    // client-side ONLY to prefill the display name/email in the consent step, but
    // we forward the RAW token to the backend, which validates it against Google's
    // tokeninfo endpoint and checks the audience. The client-decoded values are
    // never trusted by the server.
    var userObject = jwt_decode(response.credential);
    const data = {
      name: userObject.name,
      email: userObject.email,
      socialId: userObject.sub,
      token: response.credential,
      provider: "Google",
      agreeToTerms: 0,
    };
    setSocialRegisterData(data);
    setRegisterData(data);
    props.setSpinner?.(false);
  };

  useEffect(() => {
    if (!window.google || props.callFrom !== "modal") return;

    // Use the back-office-configured Google client id when present, else the
    // build-time constant. Read from initialSettings here (not the derived
    // googleClientId below, which is computed later in render) so the GIS init
    // reflects the resolved config.
    const sf = initialSettings && initialSettings.signup_flow;
    const configuredGoogle =
      sf && sf.social_logins && Array.isArray(sf.social_logins.configured)
        ? sf.social_logins.configured.find((p) => String(p.key).toLowerCase() === "google")
        : null;
    const gClientId = (configuredGoogle && configuredGoogle.client_id) || config.GoogleClientId;

    window.google.accounts.id.initialize({
      client_id: gClientId,
      callback: responseGoogle,
    });

    const mount = document.getElementById(googleButtonId);
    if (mount) {
      mount.innerHTML = "";
      window.google.accounts.id.renderButton(mount, {
        theme: "outline",
        type: "icon",
        shape: "circle",
        size: "small",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleButtonId, props.callFrom, variant, initialSettings]);

  const triggerGoogleLogin = () => {
    window.google.accounts.id.prompt();
  };

  const [socialLoginData, setSocialLoginData] = useState({});
  const [applyLogin, setApplyLogin] = useState(false);

  const checkAvailability = async () => {
    if (socialRegisterData.email.length > 0) {
      var response = await api.post("/check-social-availability", socialRegisterData);

      if (response.data.status == "Not Found") {
        setShowSocialRegTerms(true);
      } else if (
        response.data.status == "Already Registered" ||
        response.data.status == "Email Already Registered"
      ) {
        setSocialLoginData({
          email: socialRegisterData.email,
          socialId: socialRegisterData.socialId,
          provider: socialRegisterData.provider,
        });

        const loginPayload = {
          email: socialRegisterData.email,
          socialId: socialRegisterData.socialId,
          token: socialRegisterData.token,
          provider: socialRegisterData.provider,
        };
        setLoginData(loginPayload);
        socialLoginAction(loginPayload);
        setApplyLogin(true);
      } else {
        setSpinner(false);
      }
    }
  };

  useEffect(() => {
    if (socialRegisterData.provider != "") {
      checkAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialRegisterData]);

  const [errors, setErrors] = useState({});
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    socialId: "",
    provider: "",
    agreeToTerms: 0,
    acceptCountryLimitation: 0,
  });

  const updateRegisterForm = (field, value) => {
    setRegisterData({ ...registerData, [field]: value });
  };

  const registerInputChange = (ev) => {
    var target = ev.target;
    var field = target.name;
    var value = target.value;

    if (field == "agreeToTerms") {
      value = ev.target.checked ? 1 : 0;
    }
    if (field == "acceptCountryLimitation") {
      value = ev.target.checked ? 1 : 0;
    }

    updateRegisterForm(field, value);
  };

  const registerValidateForm = () => {
    var valid = true;
    var errorsTmp = {};

    if (!registerData.agreeToTerms) {
      errorsTmp = {
        ...errorsTmp,
        agreeToTerms: __("Please read & accept terms & privacy policy"),
      };
      valid = false;
    }

    setErrors(errorsTmp);
    return valid;
  };

  const registrationAction = async (ev) => {
    ev.preventDefault();
    var valid = registerValidateForm();
    if (valid) {
      var response = await api.post("/social-signup", buildSocialSignupPayload(registerData));
      if (response.data.status == "success") {
        setApplyLogin(true);
        const loginPayload = {
          email: registerData.email,
          socialId: registerData.socialId,
          token: registerData.token,
          provider: registerData.provider,
        };
        setLoginData(loginPayload);
        socialLoginAction(loginPayload);
      } else {
        setErrors(response.data.errors);
      }
    }
  };

  // Registry/config-driven: only providers enabled for this brand render.
  // Falls back to the legacy per-provider flags when signup_flow is absent.
  const configuredProviders = getConfiguredSocialProviders(initialSettings);
  const configuredByKey = {};
  configuredProviders.forEach((p) => {
    configuredByKey[String(p.key).toLowerCase()] = p;
  });

  // Merge the two enable sources: the legacy per-provider flags AND the new
  // per-provider config table (exposed as social_logins.configured). The new
  // back-office screen writes the table, so a provider enabled there must light up
  // its capitalised gate key (google -> Google) or the button would never render.
  const enabledProviders = { ...getEnabledSocialProviders(initialSettings) };
  configuredProviders.forEach((p) => {
    const cap = String(p.key).charAt(0).toUpperCase() + String(p.key).slice(1);
    enabledProviders[cap] = true;
  });

  // Prefer the back-office-configured Facebook app id over the build-time
  // constant (the Google client id is resolved the same way inside the GIS
  // effect above), so an admin can repoint a provider without a redeploy.
  const facebookAppId = (configuredByKey.facebook && configuredByKey.facebook.client_id) || config.FBAppId;

  // Authorization-code providers (Discord, Twitch, Kick, X) come from the backend
  // with their public config. Each button starts a server-verified OAuth redirect.
  const codeFlowProviders = configuredProviders.filter((p) => {
    const meta = getProviderMeta(p.key);
    return meta && meta.flow === "code" && p.client_id && p.redirect_uri;
  });

  const [codeFlowError, setCodeFlowError] = useState("");
  // A code-flow provider awaiting the 18+/terms consent that must be captured
  // before the OAuth redirect (so a first-time account is only created with it).
  const [pendingCodeProvider, setPendingCodeProvider] = useState(null);
  const [codeConsent, setCodeConsent] = useState(false);

  const startProviderRedirect = (providerConfig) => {
    // Do not redirect yet: capture consent first.
    setCodeFlowError("");
    setCodeConsent(false);
    setPendingCodeProvider(providerConfig);
  };

  const confirmCodeFlowConsent = async () => {
    if (!codeConsent || !pendingCodeProvider) {
      setCodeFlowError(__("Please confirm you are 18 or older and accept the terms."));
      return;
    }
    try {
      setCodeFlowError("");
      await startCodeFlow(pendingCodeProvider, {
        agreeToTerms: 1,
        acceptCountryLimitation: 1,
      });
    } catch (e) {
      setCodeFlowError(__("Could not start login. Please try again."));
    }
  };

  const providerIconClass = {
    discord: "fab fa-discord",
    twitch: "fab fa-twitch",
    kick: "fas fa-bolt",
    x: "fab fa-x-twitter",
  };

  const isBetmodeLayout = variant === "betmode" || variant === "streamlined";
  const socialAreaClass =
    "socialLoginArea" +
    (isBetmodeLayout ? " socialLoginArea--betmode" : "");

  // Rail order: the authorization-code providers are pinned to the confirmed
  // sequence (Discord, Twitch, Kick, X) so the row reads google, facebook,
  // discord, twitch, kick, x regardless of the back-office config order. The
  // classic variant keeps the untouched backend order.
  const codeFlowForRender = isBetmodeLayout
    ? [...codeFlowProviders].sort((a, b) => {
        const ra = BETMODE_CODE_ORDER.indexOf(a.key);
        const rb = BETMODE_CODE_ORDER.indexOf(b.key);
        return (ra === -1 ? 99 : ra) - (rb === -1 ? 99 : rb);
      })
    : codeFlowProviders;

  return (
    <>
      <div className={socialAreaClass}>
        {!showSocialRegTerms && !pendingCodeProvider && (
          <>
            {enabledProviders.Google ? (
              <>
                {props.callFrom == "banner" && (
                  <div className="googleLoginBtn">
                    <div id="bannerGoogleLogin" className="googleLoginButton"></div>
                    <div className="clearfix"></div>
                  </div>
                )}
                {props.callFrom == "modal" && isBetmodeLayout ? (
                  <>
                    <button
                      type="button"
                      className="betmode-auth__social-btn betmode-auth__social-btn--google"
                      aria-label={__("Google")}
                      style={{ "--sl-brand": BETMODE_BRAND.google, "--b": BETMODE_BRAND.google }}
                      onClick={triggerGoogleLogin}
                    >
                      {renderBetmodeIcon("google")}
                    </button>
                    <div id={googleButtonId} className="betmode-auth__google-mount" aria-hidden="true" />
                  </>
                ) : null}
                {props.callFrom == "modal" && !isBetmodeLayout ? (
                  <div onClick={triggerGoogleLogin} className="googleLoginBtn">
                    <div id={googleButtonId} className="googleLoginButton"></div>
                    <div className="clearfix"></div>
                  </div>
                ) : null}
              </>
            ) : null}
            {enabledProviders.Facebook ? (
              isBetmodeLayout && props.callFrom == "modal" ? (
                <FacebookLogin
                  appId={facebookAppId}
                  autoLoad={false}
                  fields="name,email,picture"
                  textButton=""
                  callback={responseFacebook}
                  onClick={componentClicked}
                  cssClass="betmode-auth__social-btn betmode-auth__social-btn--facebook"
                  buttonStyle={{ "--sl-brand": BETMODE_BRAND.facebook, "--b": BETMODE_BRAND.facebook }}
                  // react-facebook-login wraps the button in an unclassed <span>.
                  // Without this, that span is the rail's flex item (not the button)
                  // and collapses to icon width while the other tiles stretch, so the
                  // wrapper must itself grow and lay its button out as a flex child.
                  containerStyle={{ flex: "1 1 0", minWidth: 44, display: "flex" }}
                  icon={renderBetmodeIcon("facebook", true)}
                />
              ) : (
                <div className="facebookLoginBtn">
                  <FacebookLogin
                    appId={facebookAppId}
                    autoLoad={false}
                    fields="name,email,picture"
                    textButton=""
                    callback={responseFacebook}
                    onClick={componentClicked}
                    cssClass="login_btn facebook_btn"
                    icon="fab fa-facebook facebook_icon"
                  ></FacebookLogin>
                </div>
              )
            ) : null}

            {codeFlowForRender.map((p) => {
              const meta = getProviderMeta(p.key);
              const iconClass = providerIconClass[p.key] || "fas fa-right-to-bracket";
              if (isBetmodeLayout && props.callFrom == "modal") {
                const brand = BETMODE_BRAND[p.key] || meta.color;
                return (
                  <button
                    key={p.key}
                    type="button"
                    className={"betmode-auth__social-btn betmode-auth__social-btn--" + p.key}
                    aria-label={meta.label}
                    onClick={() => startProviderRedirect(p)}
                    style={{ "--sl-brand": brand, "--b": brand }}
                  >
                    {renderBetmodeIcon(p.key)}
                  </button>
                );
              }
              return (
                <div key={p.key} className={"socialCodeLoginBtn socialCodeLoginBtn--" + p.key}>
                  <button
                    type="button"
                    className="login_btn social_code_btn"
                    aria-label={meta.label}
                    onClick={() => startProviderRedirect(p)}
                    style={{ "--sl-brand": meta.color }}
                  >
                    <i className={iconClass + " social_code_icon"} aria-hidden="true" />
                  </button>
                </div>
              );
            })}

            {codeFlowError ? <div className="errorMsg socialCodeError">{codeFlowError}</div> : null}
          </>
        )}

        {pendingCodeProvider && (
          <div className="registerTermsArea">
            <div className="form_area">
              <div className="common_headline text-center">
                {__("Continue with :provider", { provider: getProviderMeta(pendingCodeProvider.key)?.label || pendingCodeProvider.key })}
              </div>
              <div className="input_item">
                <div className="reg_check_area">
                  <input
                    type="checkbox"
                    className="stepTerms"
                    id={"codeFlowConsent-" + googleButtonId}
                    checked={codeConsent}
                    onChange={(e) => setCodeConsent(e.target.checked)}
                  />
                  <label htmlFor={"codeFlowConsent-" + googleButtonId} className="check_area">
                    <div className="check_check_and_text_area">
                      <div className="check_box">
                        <div className="check_checked_box"></div>
                      </div>
                      <div className="check_text">
                        {__(
                          "I declare that I am 18 years or older, located in a permitted territory and have no self-exclusions."
                        )}
                        <span className="requried_icon">*</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              {codeFlowError ? <div className="errorMsg">{codeFlowError}</div> : null}
              <div className="login_button">
                <Button onClick={confirmCodeFlowConsent} className="commonBtn pTwoBtn">
                  {__("Continue")}
                </Button>
                <Button
                  onClick={() => { setPendingCodeProvider(null); setCodeFlowError(""); }}
                  className="commonBtn pTwoBtn"
                  style={{ marginLeft: 8, opacity: 0.7 }}
                >
                  {__("Cancel")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {showSocialRegTerms && (
          <div className="registerTermsArea">
            <div className="form_area">
              <div className="common_headline text-center">
                {__("Please agree to the following terms")}
              </div>
              <div className="input_item">
                <div className="reg_check_area">
                  <input
                    type="checkbox"
                    className="stepTerms"
                    id={"socialAgeConfirmation-" + googleButtonId}
                    name="agreeToTerms"
                    onChange={registerInputChange}
                  />
                  <label htmlFor={"socialAgeConfirmation-" + googleButtonId} className="check_area">
                    <div className="check_check_and_text_area">
                      <div className="check_box">
                        <div className="check_checked_box"></div>
                      </div>
                      <div className="check_text">
                        {__(
                          "I declare that I am 18 years or older, located in a permitted territory and have no self-exclusions."
                        )}
                        <span className="requried_icon">*</span>
                      </div>
                    </div>
                  </label>
                  <div className="errorMsg">{errors.agreeToTerms}</div>
                </div>
              </div>

              <div className="login_button">
                <Button onClick={registrationAction} className="commonBtn pTwoBtn">
                  {__("Confirm")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showOrDivider ? (
        <div className="or_text_section ">
          <div className="dash"></div>
          <div className="text">{__("or log in with")}</div>
          <div className="dash"></div>
        </div>
      ) : null}
    </>
  );
};

export default SocialLogin;
